import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, CurrencyField, Modal, TextField, TextareaField, fmtBRL } from "@atlas/ui/web";
import type { BeneficioDaConsignataria, PreviaAlteracaoValor } from "@atlas/sdk";
import { atlas } from "../../../lib/sdk";
import { mensagemDeErro } from "../../../lib/erro-validacao";
import { useDebouncedValue } from "../../../lib/use-debounce";
import { mesAno } from "../../../lib/beneficios";
import { competenciaOk, motivoOk } from "./encerrar-modal";
import { centavosDe } from "./incluir-modal";

// ALTERAR O VALOR DE UM BENEFÍCIO PELO PORTAL (#320).
//
// A prévia diz a diferença na margem, pela regra do órgão, e se o aumento vai
// esperar a autorização do servidor (#282) — aí o valor de antes segue
// descontando até ele autorizar.
//
// O mesmo modal responde o PEDIDO DE ALTERAÇÃO do servidor ("alterar e
// atender"): quem chama troca a prévia e a confirmação pelas do pedido, e o
// pedido é atendido na mesma operação.

type BeneficioParaAlterar = Pick<BeneficioDaConsignataria, "id" | "rotulo" | "valorCentavos" | "nome" | "matricula" | "orgao">;

export function AlterarValorBeneficioModal({
  beneficio,
  onFechar,
  onFeito,
  titulo = "Alterar valor",
  rotuloConfirmar = "Alterar valor",
  aviso,
  carregarPrevia,
  confirmar,
}: {
  beneficio: BeneficioParaAlterar;
  onFechar: () => void;
  onFeito: (aviso: string) => void;
  titulo?: string;
  rotuloConfirmar?: string;
  /** Uma linha a mais no topo — ex.: o que o servidor pediu. */
  aviso?: string;
  carregarPrevia?: (q: { competenciaInicio?: string; valorCentavos?: number }) => Promise<PreviaAlteracaoValor>;
  confirmar?: (b: { competenciaInicio: string; valorCentavos: number; motivo: string }) => Promise<{
    aguardandoAutorizacao?: boolean;
    movimentoDesatualizado?: boolean;
  }>;
}) {
  const qc = useQueryClient();
  const [inicio, setInicio] = useState("");
  const [valor, setValor] = useState<number | null>(null);
  const [motivo, setMotivo] = useState("");
  const [padraoAplicado, setPadraoAplicado] = useState(false);

  const valorCentavos = centavosDe(valor);
  const valorDebounced = useDebouncedValue(valorCentavos);
  const inicioValido = competenciaOk(inicio) ? inicio : "";
  const previa = useQuery({
    queryKey: ["banco", "previa-valor", beneficio.id, inicioValido, valorDebounced],
    queryFn: () => {
      const q = { competenciaInicio: inicioValido || undefined, valorCentavos: valorDebounced || undefined };
      return carregarPrevia ? carregarPrevia(q) : atlas.banco.beneficios.previaValor(beneficio.id, q);
    },
    enabled: inicio === "" || competenciaOk(inicio),
    placeholderData: keepPreviousData,
    retry: false,
  });
  useEffect(() => {
    if (padraoAplicado || !previa.data) return;
    setPadraoAplicado(true);
    setInicio(previa.data.competenciaInicioPadrao);
  }, [padraoAplicado, previa.data]);

  const p = previa.data && !previa.isPlaceholderData && previa.data.competenciaInicio === inicio ? previa.data : null;
  const valido =
    p !== null &&
    !p.cortePassou &&
    valorCentavos > 0 &&
    valorCentavos !== beneficio.valorCentavos &&
    valorDebounced === valorCentavos &&
    motivoOk(motivo);

  const salvar = useMutation({
    mutationFn: () => {
      const b = { competenciaInicio: inicio, valorCentavos, motivo: motivo.trim() };
      return confirmar ? confirmar(b) : atlas.banco.beneficios.alterarValor(beneficio.id, b);
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["banco", "beneficios"] });
      void qc.invalidateQueries({ queryKey: ["banco", "operadora"] });
      void qc.invalidateQueries({ queryKey: ["banco", "pedidos-cancelamento"] });
      const base = r.aguardandoAutorizacao
        ? "Aumento registrado: aguarda a autorização do servidor. Até lá, segue descontando o valor atual."
        : `Valor alterado: ${fmtBRL(valorCentavos / 100)} a partir de ${mesAno(inicio)}.`;
      onFeito(r.movimentoDesatualizado ? `${base} O movimento desta competência já foi emitido: a alteração só vale numa retificação.` : base);
      onFechar();
    },
  });

  return (
    <Modal
      open
      title={titulo}
      onClose={onFechar}
      footer={
        <>
          <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
          <Button onClick={() => salvar.mutate()} disabled={!valido || salvar.isPending}>
            {salvar.isPending ? "Alterando…" : rotuloConfirmar}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <p style={{ margin: 0 }}>
          {beneficio.rotulo} de {beneficio.nome ?? `matrícula ${beneficio.matricula}`} — {beneficio.orgao}. Hoje:{" "}
          {fmtBRL(beneficio.valorCentavos / 100)} por mês.
        </p>
        {aviso ? <p style={{ margin: 0, fontSize: 13, color: "var(--text-muted)" }}>{aviso}</p> : null}
        <CurrencyField label="Novo valor mensal" value={valor} onValueChange={setValor} />
        <TextField
          label="A partir de"
          value={inicio}
          placeholder="AAAAMM"
          onChange={(e) => setInicio(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />

        <section aria-label="Prévia da alteração" style={{ padding: 12, borderRadius: 10, background: "var(--bg-elev-2)", display: "grid", gap: 6 }}>
          {previa.isError ? (
            <span role="alert">{mensagemDeErro(previa.error, "Não foi possível calcular a prévia.")}</span>
          ) : p ? (
            <>
              {valorCentavos > 0 ? <strong>{p.mensagem}</strong> : <span style={{ color: "var(--text-muted)" }}>Informe o novo valor.</span>}
              {p.cortePassou ? (
                <span role="alert" style={{ color: "var(--danger-500)" }}>
                  O corte de {mesAno(p.competenciaInicio)} já passou: escolha uma competência mais adiante.
                </span>
              ) : null}
            </>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>{inicio === "" || competenciaOk(inicio) ? "Calculando…" : "Informe a competência (AAAAMM)."}</span>
          )}
        </section>

        <TextareaField
          label="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          hint={confirmar ? "Fica no histórico do benefício e vai na resposta ao servidor." : "Fica no histórico do benefício."}
        />
        {salvar.isError && <p role="alert">{mensagemDeErro(salvar.error, "Não foi possível alterar o valor.")}</p>}
      </div>
    </Modal>
  );
}
